import {
  Box,
  Text,
  Container,
  Image,
  Center,
  Heading,
  Button,
  Icon,
  Stack,
  Flex,
} from '@chakra-ui/react';
import Row from '@inspire/ui/chakra/layout/Row';
import { lightBeigeBg, lightYellowBg } from '@inspire/ui/static';
import {
  emphasisButtonProps,
  roundButtonProps,
} from '@inspire/ui/chakra/theme';
import { RightArrowIcon } from '@inspire/ui/icons';
import GenericHeroSection from 'components/landing-pages/HeroSection';
import Layout from 'components/layout/Layout';
import LinkButton from 'components/general/LinkButton';
import { OnlyLoggedOut } from 'components/auth/Redirects';
import TestimonialsSlider from 'components/landing-pages/TestimonialsSlider';
import { accessSSO } from 'lib/sso';
import { CONNEXION_PATH, LOGGED_IN_HOME_PATH } from 'lib/paths';
import { useMemoryStore } from 'lib/store';
import { cdnURL } from '@inspire/util/cdn';

const hero = {
  title: 'L’orientation au lycée : un défi du quotidien',
  subtitle: '',
  Animation: () => (
    <Box
      my={5}
      mx="auto"
      width={{ sm: '90%', md: '80%' }}
      lineHeight={0}
      maxW={['300px', null, '390px']}
    >
      <Center>
        <Image
          h="200px"
          src="/img/landing-pages/main-prof.svg"
          alt="Équipe pédagogique"
        />
      </Center>
    </Box>
  ),
  cta: 'Je veux essayer !',
  onCTAClick: () => {
    // TODO: report?
  },
  text: (
    <>
      <Text>
        Encore beaucoup d’élèves n’ont pas de projet précis au moment où ils
        obtiennent leur bac.
      </Text>
      <Text>
        Les équipes pédagogiques qui les suivent jouent un{' '}
        <b>rôle central dans l’accompagnement</b> de ces élèves dans leur projet
        professionnel. Cela demande du temps et les sources d’information sont
        nombreuses, complexes et parfois inadaptées aux élèves. C’est cet accès
        à l’information que nous avons voulu simplifier.
      </Text>
      <Text>
        Nos <b>modules pédagogiques</b> vous permettent d’accompagner simplement
        (et gratuitement) les élèves au fil des ans, peu importe le niveau
        d’aboutissement de leur projet post-bac.
      </Text>
      <Text mb={0}>
        <b>
          Aidez-les à se découvrir via des modules courts et ludiques à réaliser
          en classe !
        </b>
      </Text>
    </>
  ),
};

const testimonials = [
  {
    _id: 1,
    imageUrl: '/img/landing-pages/temoignages/prof1.jpg',
    description:
      'Les élèves ont tout de suite adhéré, ils ont été moteurs et dynamiques. Les modules les mettent en action.',
  },
  {
    _id: 2,
    imageUrl: '/img/landing-pages/temoignages/prof2.jpg',
    description:
      "J’ai constaté que l'outil numérique aide au niveau de l'attention de mes élèves, ils étaient très concentrés.",
  },
  {
    _id: 3,
    imageUrl: '/img/landing-pages/temoignages/prof3.jpg',
    description:
      "Ces modules m'ont donné plein de pistes pour animer des heures complètes dédiées à l'orientation. J’ai gagné un temps de fou !",
  },
];

const VoieProfessionnelleLandingPage = () => {
  // Get the redirect URL from the store for redirection after login
  const redirectTargetUrl = useMemoryStore((s) => s.redirectTargetUrl);
  const redirectUrlFromStore = redirectTargetUrl?.redirectUrl;

  return (
    <>
      <Box bg="white">
        {/* Header compensation */}
        <Row pt={['56px', '64px']}>
          <Container maxW="1000px" py={12}>
            <GenericHeroSection {...hero} />
          </Container>
        </Row>

        <Row bgColor={lightYellowBg} py={12}>
          <Container maxW="container.lg">
            <Heading as="h2" textAlign="center" mt={0} mb={6}>
              Des modules pédagogiques clé en main
            </Heading>
            <Text fontStyle="italic" textAlign="center">
              Un outil d'e-learning destiné à être utilisé en classe de Première
              et Terminale
            </Text>

            <Stack my={12} spacing={12} direction={['column', 'row']}>
              <Box backgroundColor="white" borderRadius={20} p={4}>
                <Text textAlign="center">
                  Des <b>activités dynamiques</b> mêlant différents formats
                  (vidéos, infographies, questionnaires, quiz, réflexion
                  collective…).
                </Text>
              </Box>
              <Box backgroundColor="white" borderRadius={20} p={4}>
                <Text textAlign="center">
                  Etudes sup’, vie étudiante, codes du monde professionnel,
                  candidatures... tout est là pour vous outiller au quotidien.
                </Text>
              </Box>
              <Box backgroundColor="white" borderRadius={20} p={4}>
                <Text textAlign="center">
                  Adaptés aussi bien pour les projets de{' '}
                  <b>poursuite d’études que d’insertion professionnelle</b>
                </Text>
              </Box>
              <Box backgroundColor="white" borderRadius={20} p={4}>
                <Text textAlign="center">
                  <b>Complémentaires</b> à l’accompagnement des équipes
                  pédagogiques{' '}
                  <b>en fonction de vos besoins et ceux de vos élèves</b>
                </Text>
              </Box>
            </Stack>

            <Center>
              <Image
                alt="Voie professionnelle"
                maxW={['100%', 600]}
                src="/img/gif/voie-prof.gif"
              />
            </Center>

            <Center my={20}>
              <Stack spacing="8" direction={['column', 'row']}>
                <LinkButton
                  href={cdnURL('/2526-Modules.pdf')}
                  colorScheme="black"
                  variant="outline"
                  {...roundButtonProps}
                >
                  Télécharger le descriptif de nos modules
                </LinkButton>
                <Button
                  {...roundButtonProps}
                  {...emphasisButtonProps}
                  onClick={() => {
                    // pushEvent(
                    //   GTM_CATEGORY_LANDING_PAGES,
                    //   pageConfig.profil === 'prof'
                    //     ? GTM_ACTION_LANDING_PAGE_PROF
                    //     : pageConfig.profil === 'lyceen'
                    //     ? GTM_ACTION_LANDING_PAGE_LYCEEN
                    //     : GTM_ACTION_LANDING_PAGE_STUDENT,
                    //   "Je m'inscris"
                    // );
                    accessSSO({
                      targetUrl: redirectUrlFromStore
                        ? `${CONNEXION_PATH}?redirectUrl=${redirectUrlFromStore}`
                        : `${CONNEXION_PATH}?redirectUrl=${LOGGED_IN_HOME_PATH}`,
                    });
                  }}
                >
                  Je m'inscris <Icon ml={2} as={RightArrowIcon} />
                </Button>
              </Stack>
            </Center>
          </Container>
        </Row>

        <Row py={12}>
          <Container maxW="container.lg">
            <Heading as="h2" textAlign="center" mt={0} mb={10}>
              Gagnez du temps
            </Heading>

            <Stack direction={['column', 'row']}>
              <Text maxW="500px" mr={[0, '50px']} mb={['50px', 0]}>
                Nous savons que votre temps est précieux !<br />
                <br />
                <br />
                Les modules pédagogiques ont été pensés pour vous faire gagner
                du temps dans la préparation de vos heures d’accompagnement à
                l’orientation.
              </Text>
              <Stack>
                {[
                  'Parcourez les modules disponibles',
                  'Sélectionnez les modules en fonction de vos besoins',
                  'Réalisez-les en classe avec vos élèves',
                ].map((t, i) => (
                  <Box
                    backgroundColor="#C6F6D5"
                    pr={4}
                    borderRadius={999}
                    key={`${i}`}
                    w={'w-100'}
                  >
                    <Flex position="relative">
                      <Text
                        ml={4}
                        backgroundColor="white"
                        px="4"
                        py="2"
                        fontWeight="bold"
                        borderRadius="999px"
                        boxSizing="initial"
                        height="fit-content"
                      >
                        {i + 1}
                      </Text>

                      <Text pt={2} ml={4}>
                        {t}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            </Stack>

            <Center my={20}>
              <Stack spacing="8" direction={['column', 'row']}>
                <Button
                  {...roundButtonProps}
                  {...emphasisButtonProps}
                  onClick={() => {
                    // pushEvent(
                    //   GTM_CATEGORY_LANDING_PAGES,
                    //   pageConfig.profil === 'prof'
                    //     ? GTM_ACTION_LANDING_PAGE_PROF
                    //     : pageConfig.profil === 'lyceen'
                    //     ? GTM_ACTION_LANDING_PAGE_LYCEEN
                    //     : GTM_ACTION_LANDING_PAGE_STUDENT,
                    //   "Je m'inscris"
                    // );
                    accessSSO({
                      targetUrl: CONNEXION_PATH,
                    });
                  }}
                >
                  S'inscrire <Icon ml={2} as={RightArrowIcon} />
                </Button>
              </Stack>
            </Center>
          </Container>
        </Row>

        <Row pos="relative" bgColor={lightBeigeBg} py={12}>
          <Image
            w="18%"
            src="/img/landing-pages/big-arrow-flipped.png"
            pos="absolute"
            left="0"
            alt="arrow"
            display={{ base: 'none', md: 'block' }}
          />
          <Container maxW="container.lg">
            <Heading as="h2" textAlign="center" mt={0} mb={6}>
              Les aider à trouver leur voie
            </Heading>
            <Text textAlign={['center', 'right']}>
              Les modules pédagogiques visent à promouvoir l'égalité des chances
              et permettre à chaque élève,
              <br /> accompagné par ses enseignants, de trouver sa voie.
              <br />
            </Text>
            <Text pt={4} textAlign={['center', 'right']}>
              Entre accompagnement par l’enseignant et autonomie,{' '}
              <b>les élèves construisent pas à pas leur projet d’orientation</b>{' '}
              à travers des activités interactives permettant l'échange, la
              réflexion et la recherche d'informations.
            </Text>
            <Text pt={4} textAlign={['center', 'right']}>
              Le plus important pour nous : des lycéens acteurs de leur
              orientation !
            </Text>
          </Container>
        </Row>

        <Row py={12}>
          <Container maxW="container.lg">
            <Heading as="h2" textAlign="center" mt={0} mb={6}>
              Témoignages d’enseignants
            </Heading>
            <Box mb={12}>
              <TestimonialsSlider testimonials={testimonials} />
            </Box>
            <Text color="#333333" pt="8" textAlign="center">
              Nos outils sont co-construits avec les équipes pédagogiques et
              nous sommes toujours ravis d’avoir des retours !<br />
              N’hésitez pas à nous écrire à contact@inspire-orientation.org pour
              nous aider à construire et améliorer nos activités.
            </Text>
          </Container>
        </Row>
      </Box>
    </>
  );
};

VoieProfessionnelleLandingPage.getWrappers = (page) => (
  <OnlyLoggedOut>
    <Layout variant="simple" withMainRow={false}>
      {page}
    </Layout>
  </OnlyLoggedOut>
);

export default VoieProfessionnelleLandingPage;
